// React
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

// Libraries
import Joi from "joi";
import PropTypes from "prop-types";
import { Form, Button } from "semantic-ui-react";
import { isEmpty } from "lodash";

// Redux
import { connect } from "react-redux";
import { register } from "@Actions/authentication";

// Components
import Text from "@Components/Text";
import Input from "@Components/Input";

// Utils
import { getValidationErrors } from "@Utils/functionality";
import { buildAuthPath } from "@Utils";

// Styles
import styles from "./styles.module.css";

const Register = ({ dispatch }) => {
	const navigate = useNavigate();
	const params = useParams();
	const defaultErrors = {
		fullName: "",
		nickName: "",
		email: "",
		phone: "",
		birthdate: "",
		password: "",
		pin: "",
	};
	const [pin, setPin] = useState("");
	const [fullName, setFullName] = useState("");
	const [nickName, setNickName] = useState("");
	const [email, setEmail] = useState("");
	const [phone, setPhone] = useState("");
	const [password, setPassword] = useState("");
	const [token, setToken] = useState("");
	const [companyName, setCompanyName] = useState("");
	const [errors, setErrors] = useState(defaultErrors);

	const schema = Joi.object().keys({
		fullName: Joi.string().min(6).required().messages({
			"string.min": "Nombre Completo debe tener mínimo 6 caracteres",
			"string.empty": "Nombre Completo es un campo requerido",
		}),
		nickName: Joi.string().allow("").min(4).messages({
			"string.min": "Nombre de Usuario debe tener mínimo 4 caracteres",
		}),
		email: Joi.string()
			.email({ tlds: { allow: false } })
			.required()
			.messages({
				"string.email": "Email debe ser un correo electrónico válido",
				"string.empty": "Email es un campo requerido",
			}),
		phone: Joi.string()
			.required()
			.custom((value, helpers) => {
				if (/^\d+$/.test(value) && !value.startsWith("1")) {
					return helpers.message("Debes agregar el 1 delante de tu teléfono para registrarte");
				}
				return value;
			})
			.messages({
				"string.empty": "El teléfono es un campo requerido",
			}),
		birthdate: Joi.date().allow("").less("now").greater("12-31-1899").messages({
			"date.less": "Fecha de Nacimiento debe ser menor a la fecha de hoy",
			"date.greater": "Fecha de Nacimiento debe ser a partir del año 1900",
		}),
		password: Joi.string().min(4).required().messages({
			"string.min": "Contraseña debe tener mínimo 4 caracteres",
			"string.empty": "Contraseña es un campo requerido",
		}),
		token: Joi.string().allow(""),
		pin: Joi.when("token", {
			is: Joi.string().empty(""),
			then: Joi.string().min(1).messages({
				"string.empty": "Pin es un campo requerido",
			}),
			otherwise: Joi.optional(),
		}),
	});

	useEffect(() => {
		if (params.token) {
			setToken(params.token);
			setCompanyName(params.companyName);
		}
	}, [params]);

	const handleInput = (e) => {
		const { value, name } = e.target;
		setErrors((prevErrors) => {
			return {
				...prevErrors,
				[name]: "",
			};
		});
		switch (name) {
			case "fullName":
				setFullName(value);
				break;
			case "nickName":
				setNickName(value);
				break;
			case "phone":
				setPhone(value);
				break;
			case "email":
				setEmail(value);
				break;
			case "password":
				setPassword(value);
				break;
			case "pin":
				setPin(value);
				break;
			default:
				break;
		}
	};

	const cleanupForm = () => {
		setFullName("");
		setNickName("");
		setEmail("");
		setPhone("");
		setPassword("");
		setPin("");
		setToken("");
		setCompanyName("");
		setErrors(defaultErrors);
	};

	const handleRegister = () => {
		const validationData = { fullName, nickName, email, phone, password };
		if (isEmpty(token)) {
			validationData.pin = pin;
		}
		const validation = schema.validate(validationData, { abortEarly: false });
		if (validation.error) {
			setErrors(getValidationErrors(validation, defaultErrors));
		} else {
			const user = {
				fullName,
				nickName: nickName.trim() === "" ? null : nickName,
				email,
				phone,
				password,
			};
			dispatch(register(user, token || null, pin, cleanupForm, navigate));
		}
	};

	return (
		<>
			<Text className={styles.infoTitle}>Registro de Usuario</Text>
			<Form>
				{!isEmpty(token) && <Input className={styles.inputDisabled} icon="home" disabled label="Compañía" required value={companyName} />}
				{isEmpty(token) && (
					<Input
						className={styles.input}
						icon="lock"
						label="Pin"
						required
						error={!isEmpty(errors.pin) && { content: errors.pin, pointing: "above" }}
						placeholder="Pin de Compañía"
						value={pin}
						name="pin"
						onChange={handleInput}
						onKeyPress={handleRegister}
					/>
				)}
				<Input
					className={styles.input}
					icon="user"
					label="Nombre Completo"
					required
					error={!isEmpty(errors.fullName) && { content: errors.fullName, pointing: "above" }}
					placeholder="Nombre Completo"
					value={fullName}
					name="fullName"
					onChange={handleInput}
					onKeyPress={handleRegister}
				/>
				<Input
					className={styles.input}
					icon="users"
					label="Nombre de Usuario"
					error={!isEmpty(errors.nickName) && { content: errors.nickName, pointing: "above" }}
					placeholder="Nombre de Usuario"
					value={nickName}
					name="nickName"
					onChange={handleInput}
					onKeyPress={handleRegister}
				/>
				<Input
					className={styles.input}
					icon="mail"
					label="Email"
					required
					error={!isEmpty(errors.email) && { content: errors.email, pointing: "above" }}
					placeholder="Email"
					value={email}
					name="email"
					onChange={handleInput}
					onKeyPress={handleRegister}
				/>
				<Input
					className={styles.input}
					icon="phone"
					label="Teléfono"
					required
					error={!isEmpty(errors.phone) && { content: errors.phone, pointing: "above" }}
					placeholder="Teléfono"
					value={phone}
					name="phone"
					onChange={handleInput}
					onKeyPress={handleRegister}
				/>
				<Input
					isPassword
					className={styles.input}
					label="Contraseña"
					required
					autoComplete="off"
					error={!isEmpty(errors.password) && { content: errors.password, pointing: "above" }}
					value={password}
					name="password"
					onChange={handleInput}
					onKeyPress={handleRegister}
				/>
			</Form>
			<Button className={styles.button} fluid onClick={handleRegister}>
				Regístrate
			</Button>
			<Button
				className={styles.cancelButton}
				fluid
				onClick={() => {
					return navigate(buildAuthPath("/auth/login", companyName, token), { replace: true });
				}}>
				Cancelar
			</Button>
		</>
	);
};

Register.propTypes = {
	dispatch: PropTypes.func,
};

export default connect()(Register);
